import {PlainObject} from '@xh/hoist/core';
import {genDisplayName} from '@xh/hoist/data';
import {isNil, isNumber, isString, omitBy, sortBy} from 'lodash';
import {formatHtml} from '../spec/Formats';
import {FieldCatalog, FormatSpec} from '../spec/SpecTypes';
import {usesSecondaryAxis, visibleSeries} from './ChartSpecOps';
import {ChartSpec, ChartSpecAxis, ChartSpecSeries, LegendPosition, OHLC_KEYS, SeriesColor} from './ChartSpecTypes';

/** Named series colors - the only place they become values. */
export const SERIES_COLOR_VALUES: Record<SeriesColor, string> = {
    blue: '#4e79a7',
    orange: '#f28e2b',
    red: '#e15759',
    teal: '#76b7b2',
    green: '#59a14f',
    yellow: '#edc948',
    purple: '#b07aa1',
    pink: '#ff9da7',
    brown: '#9c755f',
    gray: '#9c9c9c'
};

export interface BuildChartOptions {
    /** Called for a legend click on a series - the host dispatches the change, so it lands in the spec. */
    onLegendClick?: (field: string) => void;
}

export interface BuiltChart {
    /** Highcharts config, without series. */
    config: PlainObject;
    series: PlainObject[];
}

type XAxisType = 'datetime' | 'linear' | 'category';

/** Id of the candle series - not a field, so it is never taken for one of the spec's series. */
const CANDLE_SERIES_ID = '__candle';

/**
 * Turn a ChartSpec plus the host's rows into a Highcharts config and series. This is where the
 * spec's named descriptors (formats, colors) become code - formatter functions and color values.
 */
export function buildChart(spec: ChartSpec, rows: PlainObject[], catalog: FieldCatalog, opts: BuildChartOptions = {}): BuiltChart {
    const xType = xAxisType(spec, catalog),
        built = spec.layout === 'categories' ? buildCategories(spec, rows ?? [], catalog) : buildSeries(spec, rows ?? [], catalog, xType);

    return {config: buildConfig(spec, xType, built.categories, opts), series: built.series};
}

function xAxisType(spec: ChartSpec, catalog: FieldCatalog): XAxisType {
    if (spec.layout === 'categories' || spec.type === 'pie') return 'category';
    switch (catalog.find(f => f.name === spec.xField)?.type) {
        case 'date':
            return 'datetime';
        case 'number':
            return 'linear';
        default:
            return 'category';
    }
}

//------------------------
// Series
//------------------------
interface BuiltSeries {
    series: PlainObject[];
    categories?: string[];
}

/** 'series' layout - one point per row, one series per field. */
function buildSeries(spec: ChartSpec, rows: PlainObject[], catalog: FieldCatalog, xType: XAxisType): BuiltSeries {
    const {xField} = spec,
        xFormat = spec.axes.x.format,
        xFieldType = catalog.find(f => f.name === xField)?.type,
        xOf = (r: PlainObject) => (xFieldType === 'date' ? toTime(r[xField]) : r[xField]),
        xLabel = (r: PlainObject) => (xFormat ? formatHtml(xOf(r), xFormat) : String(r[xField])),
        valid = rows.filter(r => !isNil(xOf(r))),
        // Dates and numbers in ascending order (Highcharts needs it, and FMP data comes newest first);
        // categories keep the host's row order
        sorted = xFieldType === 'date' || xFieldType === 'number' ? sortBy(valid, xOf) : valid;

    if (spec.type === 'pie') {
        const s = visibleSeries(spec)[0];
        if (!s) return {series: []};
        return {
            series: [
                {
                    id: s.field,
                    name: seriesLabel(s, catalog),
                    type: 'pie',
                    data: sorted.map(r => ({name: xLabel(r), y: toNumber(r[s.field])}))
                }
            ]
        };
    }

    const categories = xType === 'category' ? sorted.map(xLabel) : undefined,
        // The candles go first, so the series - lines, unless they say otherwise - draw over them
        candles = spec.type === 'candle' && spec.ohlc ? [buildCandles(spec, sorted, catalog, (r, idx) => (categories ? idx : xOf(r)))] : [];
    return {
        categories,
        series: candles.concat(
            spec.series.map(s =>
                omitBy(
                    {
                        id: s.field,
                        name: seriesLabel(s, catalog),
                        type: s.type,
                        yAxis: s.axis === 'secondary' ? 1 : 0,
                        color: s.color ? SERIES_COLOR_VALUES[s.color] : undefined,
                        dashStyle: s.dashed ? 'ShortDash' : undefined,
                        visible: !s.hidden,
                        data: sorted.map(r => (categories ? toNumber(r[s.field]) : [xOf(r), toNumber(r[s.field])]))
                    },
                    isNil
                )
            )
        )
    };
}

/** One candle per row that has all of its values - an 'hlc' candle has no open. */
function buildCandles(spec: ChartSpec, rows: PlainObject[], catalog: FieldCatalog, xOf: (r: PlainObject, idx: number) => any): PlainObject {
    const {ohlc, candleStyle} = spec,
        keys = OHLC_KEYS.filter(k => candleStyle !== 'hlc' || k !== 'open');

    return {
        id: CANDLE_SERIES_ID,
        name: catalog.find(f => f.name === ohlc.close)?.label ?? genDisplayName(ohlc.close),
        type: candleStyle,
        showInLegend: false,
        dataLabels: {enabled: false},
        data: rows.map((r, idx) => [xOf(r, idx), ...keys.map(k => toNumber(r[ohlc[k]]))]).filter(p => !p.includes(null))
    };
}

/** 'categories' layout - one point per field of the first row. */
function buildCategories(spec: ChartSpec, rows: PlainObject[], catalog: FieldCatalog): BuiltSeries {
    const row = rows[0] ?? {},
        entries = visibleSeries(spec);

    return {
        series: [
            {
                name: spec.axes.primary.title ?? 'Value',
                type: spec.type,
                colorByPoint: true,
                data: entries.map(s =>
                    omitBy({name: seriesLabel(s, catalog), y: toNumber(row[s.field]), color: s.color ? SERIES_COLOR_VALUES[s.color] : undefined}, isNil)
                )
            }
        ]
    };
}

export function seriesLabel(s: ChartSpecSeries, catalog: FieldCatalog): string {
    return s.name ?? catalog.find(f => f.name === s.field)?.label ?? genDisplayName(s.field);
}

//------------------------
// Config
//------------------------
function buildConfig(spec: ChartSpec, xType: XAxisType, categories: string[], opts: BuildChartOptions): PlainObject {
    const {axes, type} = spec,
        isPie = type === 'pie',
        // Points are named (pie slices, categories) rather than belonging to named series
        namedPoints = isPie || spec.layout === 'categories',
        stacking = spec.stacking !== 'none' && ['column', 'bar', 'area', 'areaspline'].includes(type) ? spec.stacking : undefined,
        formatOf = (series: any): FormatSpec => (series?.yAxis?.options?.id === 'secondary' ? axes.secondary : axes.primary).format,
        fmt = (v: any, format: FormatSpec) => (format ? formatHtml(v, format) : v);

    return {
        // The series of a candle chart are lines over the candles
        chart: {type: type === 'candle' ? 'line' : type, animation: false},
        legend: legendConfig(spec.legend),
        xAxis: omitBy(
            {
                type: xType,
                categories,
                title: {text: axes.x.title ?? null},
                labels: xType !== 'category' && axes.x.format ? {formatter: labelFormatter(axes.x.format)} : undefined,
                min: axes.x.min,
                max: axes.x.max
            },
            isNil
        ),
        yAxis: [yAxisConfig('primary', axes.primary, false, true), yAxisConfig('secondary', axes.secondary, true, usesSecondaryAxis(spec))],
        tooltip: {
            shared: spec.sharedTooltip && !namedPoints,
            xDateFormat: '%Y-%m-%d',
            pointFormatter: function () {
                const label = namedPoints ? this.name : this.series.name;
                if (this.series.options.id === CANDLE_SERIES_ID) {
                    const values = OHLC_KEYS.filter(k => !isNil(this[k])).map(k => `${k[0].toUpperCase()}: <b>${fmt(this[k], axes.primary.format)}</b>`);
                    return `<span style="color:${this.color}">●</span> ${label} - ${values.join(' ')}<br/>`;
                }
                return `<span style="color:${this.color}">●</span> ${label}: <b>${fmt(this.y, formatOf(this.series))}</b><br/>`;
            }
        },
        plotOptions: {
            series: {
                animation: false,
                stacking,
                marker: {enabled: spec.markers},
                dataLabels: {
                    enabled: spec.dataLabels !== 'none',
                    formatter: function () {
                        const name = namedPoints ? this.point.name : this.series.name,
                            value = String(fmt(this.y, formatOf(this.series)));
                        switch (spec.dataLabels) {
                            case 'name':
                                return name;
                            case 'nameAndValue':
                                return `${name}: ${value}`;
                            default:
                                return value;
                        }
                    }
                },
                events: {
                    legendItemClick: function () {
                        const field = this.options?.id;
                        if (!opts.onLegendClick || !isString(field)) return true;
                        opts.onLegendClick(field);
                        return false;
                    }
                }
            },
            pie: {
                innerSize: `${spec.innerSize}%`,
                showInLegend: spec.legend !== 'none'
            }
        }
    };
}

function yAxisConfig(id: string, axis: ChartSpecAxis, opposite: boolean, visible: boolean): PlainObject {
    return omitBy(
        {
            id,
            opposite,
            visible,
            title: {text: axis.title ?? null},
            labels: axis.format ? {formatter: labelFormatter(axis.format)} : undefined,
            min: axis.min,
            max: axis.max
        },
        isNil
    );
}

function labelFormatter(format: FormatSpec) {
    return function () {
        return formatHtml(this.value, format);
    };
}

function legendConfig(position: LegendPosition): PlainObject {
    switch (position) {
        case 'none':
            return {enabled: false};
        case 'top':
            return {enabled: true, align: 'center', verticalAlign: 'top', layout: 'horizontal'};
        case 'right':
            return {enabled: true, align: 'right', verticalAlign: 'middle', layout: 'vertical'};
        default:
            return {enabled: true, align: 'center', verticalAlign: 'bottom', layout: 'horizontal'};
    }
}

//------------------------
// Values
//------------------------
function toTime(v: any): number {
    if (isNil(v) || v === '') return null;
    if (isNumber(v)) return v;
    const ret = v instanceof Date ? v.getTime() : Date.parse(v);
    return isNaN(ret) ? null : ret;
}

function toNumber(v: any): number {
    if (isNil(v) || v === '') return null;
    const ret = Number(v);
    return isFinite(ret) ? ret : null;
}
